import type {ReactElement} from 'react';
import React from 'react';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromLineItem} from '@Hooks/poster-editor/useGetKeyFromLineItem';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import styles from './line-cap-style-option.module.scss';
import {Option} from '@Components/contextual-menu/components/option';
import {Dropdown} from '@Components/dropdown-v2';
import {LineCapStyle} from '@PosterWhiteboard/items/line-item/line-item.types';
import type {LineCapStyleOptionsPopup} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-cap-style-option/line-cap-style-option.types';
import {LineCapStyleOptionTypes} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-cap-style-option/line-cap-style-option.types';
import {getSvgFromPointStyle} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-cap-style-option/line-cap-style-svgs';
import {getOptionsArray} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-cap-style-option/line-cap-style-option.library';

export function LineCapStyleOption(): ReactElement {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemLineCapStart = useGetKeyFromLineItem('lineCapStart');
  const activeItemLineCapEnd = useGetKeyFromLineItem('lineCapEnd');

  if (!activeItem || activeItemLineCapStart === undefined || activeItemLineCapEnd == undefined) {
    throw new Error("item doesn't have activeItemLineCapStart or activeItemLineCapEnd property");
  }
  const onClickOption = (option: LineCapStyle, lineCap: LineCapStyleOptionTypes): void => {
    const lineCapType = lineCap === LineCapStyleOptionTypes.LINE_START ? activeItemLineCapStart : activeItemLineCapEnd;
    if (lineCapType === option) {
      return;
    }

    const updateInfo = lineCap == LineCapStyleOptionTypes.LINE_START ? {lineCapStart: option} : {lineCapEnd: option};

    void activeItem.updateFromObject(updateInfo, {
      undoable: true,
    });
  };

  const getOptions = (lineCapType: LineCapStyleOptionTypes): ReactElement => {
    const options = getOptionsArray();
    const lineCapTypeCurrentValue = lineCapType === LineCapStyleOptionTypes.LINE_START ? activeItemLineCapStart : activeItemLineCapEnd;
    return (
      <>
        {options.map((option) => {
          return (
            <li
              key={option.id}
              id={option.id}
              onClick={(): void => {
                onClickOption(option.id, lineCapType);
              }}
              className={`${styles.svgContainer} ${styles.svgButton} ${lineCapTypeCurrentValue === option.id ? styles.selected : ''} flex-center   radius-4`}
            >
              {getSvgFromPointStyle(option.id, lineCapType, lineCapTypeCurrentValue === option.id)}
            </li>
          );
        })}
      </>
    );
  };
  const getOptionsPopup = (lineCapType: LineCapStyleOptionTypes): ReactElement => {
    return (
      <div className={styles.optionsPopupContainer}>
        <ul className={`${styles.optionsPopup} flexbox spacing-m-0`}>{getOptions(lineCapType)}</ul>
      </div>
    );
  };
  const getLineCapStart = (): LineCapStyleOptionsPopup => {
    return {
      id: LineCapStyleOptionTypes.LINE_START,
      dropdownPopup: getOptionsPopup(LineCapStyleOptionTypes.LINE_START),
      alignment: ALIGNMENT_TYPE.BOTTOM,
    };
  };

  const getLineCapEnd = (): LineCapStyleOptionsPopup => {
    return {
      id: LineCapStyleOptionTypes.LINE_END,
      dropdownPopup: getOptionsPopup(LineCapStyleOptionTypes.LINE_END),
      alignment: ALIGNMENT_TYPE.BOTTOM,
    };
  };

  const getLineCapStyleData = (): LineCapStyleOptionsPopup[] => {
    return [getLineCapStart(), getLineCapEnd()];
  };

  const getDropdownSelector = (selectedOption: LineCapStyleOptionTypes): ReactElement => {
    const lineCap = selectedOption === LineCapStyleOptionTypes.LINE_START ? activeItemLineCapStart : activeItemLineCapEnd;
    const svgType = lineCap !== LineCapStyle.NONE ? lineCap : LineCapStyle.ARROW;
    return (
      <div className={`${styles.selectorContainer} ${lineCap !== LineCapStyle.NONE ? styles.selected : ''} radius-4 border-s-standard`}>
        <div className={`${styles.svgSelectorContainer} flex-row-justify-center flex-row-align-center spacing-m-2`}>
          {getSvgFromPointStyle(svgType, selectedOption, lineCap !== LineCapStyle.NONE)}
        </div>
      </div>
    );
  };

  const getLineCapStyleDropdown = (): ReactElement => {
    const lineCapStyleData = getLineCapStyleData();
    return (
      <div className={`flex-h-row ${styles.dropdownGroupGap} `}>
        {lineCapStyleData.map((data) => {
          return (
            <Dropdown
              key={data.id}
              popUpHasCustomWidth
              popUpHasCustomHeight
              alignment={ALIGNMENT_TYPE.BOTTOM_END}
              selector={getDropdownSelector(data.id)}
              popup={getOptionsPopup(data.id)}
              popupClassName={`border-s-standard`}
              className={`${styles.dropdownContainer} radius-4`}
            />
          );
        })}
      </div>
    );
  };

  return <Option key="line-ends-option" label={window.i18next.t('pmwjs_line_ends')} optionControl={getLineCapStyleDropdown()} />;
}
